import { XMLParser } from 'fast-xml-parser';
import isEmpty from 'lodash/isEmpty';

import {
  CloseBoldIcon,
  DoubleArrowIcon,
  FavoriteFilledIcon,
  formatData,
  SortUpIcon,
  SquareIcon,
} from '@utilities';

import { cardGridItem, createCanvasFromElement, treeItem } from '@utilities/powerpoint';

import {
  renderDoubleTwoNodesConnector,
  renderFourNodesConnector,
  renderRightTwoNodesConnector,
  renderThreeNodesConnector,
  renderTwoNodesConnector,
} from '../components/Tree/components/Connector';

import { getSanitizedFilename } from './helpers';
import {
  DECOMPOSITION_TREE_TYPE,
  getDecompositionNodeColor,
  getDecompositionPctChange,
  transformDecompositionTreeRows,
} from './decomposition_tree_helpers';

/**
 * get node's askWhy option
 * @param  {string} nodeId
 * @param  {Object} askWhyPeopleGroups
 * @return {Object}
 */
const getAskWhyOption = (nodeId, askWhyPeopleGroups) => {
  return askWhyPeopleGroups?.find((askWhyGroup) => askWhyGroup?.id === nodeId);
};

/**
 * get node's people group option
 * @param  {string} nodeId
 * @param  {Object} peopleGroupTypes
 * @return {Object}
 */
const getPeopleGroupOption = (nodeId, peopleGroupTypes) => {
  return peopleGroupTypes?.find((peopleGroupType) => peopleGroupType?.id === nodeId);
};

/**
 * transforms and formats Card report data
 * @param  {Array} options.askWhyPeopleGroups
 * @param  {XML} options.jsx
 * @param  {Array} options.peopleGroupTypes
 * @param  {Array} options.reportData
 * @return {Object}
 */
export const transformCardReportData = ({
  askWhyPeopleGroups,
  jsx,
  peopleGroupTypes,
  reportData = [],
}) => {
  const parser = new XMLParser({
    attributeNamePrefix: '',
    attributesGroupName: '$',
    ignoreAttributes: false,
    ignoreDeclaration: true,
    isArray: (_name, _jpath, _isLeafNode, isAttribute) => !isAttribute,
  });
  const tab = parser.parse(jsx).Tab?.[0];

  const {
    colLabels: labels = {},
    data = [],
    id = '',
    metadata: {
      context_variables: contextVariables = {},
      people_group_options: peopleGroupOptions,
    } = {},
    name = '',
    tooManyRows = '',
  } = reportData[0] || {};

  const cardsLayout = tab?.CardsLayout?.length > 0 ? tab?.CardsLayout?.[0] : {};
  const cardsGroup = cardsLayout?.CardsGroup?.[0]?.Card?.[0];

  return {
    cardBlocks: cardsLayout?.CardsBlock
      ? cardsLayout?.CardsBlock?.map((cardBlock) => {
          return cardBlock.Card?.map((block) => {
            const {
              $: { color = '', description = '', hasStatusBar = '', id = '', title = '' },
              CardPanels: cardPanels,
            } = block;

            const cardData = data?.find((d) => id === d['card']);
            const cardId = cardData?.['card'] || id;
            const peopleGroupId = peopleGroupOptions?.[cardId];
            const nodePeopleGroupOption = peopleGroupId
              ? getPeopleGroupOption(peopleGroupId, peopleGroupTypes)
              : null;
            const nodeAskWhyOption = peopleGroupId
              ? getAskWhyOption(peopleGroupId, askWhyPeopleGroups)
              : null;

            return {
              color,
              description: contextVariables?.[description] ? contextVariables?.[description] : '',
              hasStatusBar,
              id,
              level: cardData?.hml_shift ? cardData?.hml_shift : '',
              nodeAskWhyOption,
              nodePeopleGroupOption: nodePeopleGroupOption ? nodePeopleGroupOption : null,
              priority: cardData?.heavy_med_light ? cardData?.heavy_med_light : '',
              title: contextVariables?.[title] ? contextVariables?.[title] : '',
              treeRows: cardPanels?.map((cardPanel) => {
                const {
                  $: { content = '', orientation = '' },
                  CardPanel: panels,
                } = cardPanel;
                return {
                  content,
                  orientation,
                  treeCol: panels.map((card) => {
                    const {
                      $: {
                        color = '',
                        description = '',
                        inline = '',
                        label = '',
                        type = '',
                        valueNo = '',
                        valueYes = '',
                      },
                      CardRow: cardRows,
                    } = card;
                    let values = [];
                    return {
                      cardRows: cardRows
                        ? cardRows?.map((row) => {
                            return {
                              cardCol: row?.CardCol?.filter(
                                (col) => col !== '' && !isEmpty(col)
                              ).map((col) => {
                                const {
                                  $: { format = '', id = '' },
                                } = col;

                                const title =
                                  cardData?.[id] === 0
                                    ? '-'
                                    : cardData?.[id]
                                      ? formatData({
                                          format: format,
                                          value: cardData?.[id],
                                        })
                                      : '';
                                values.push(title);
                                return {
                                  description: labels?.[id] ? labels?.[id] : '',
                                  title,
                                };
                              }),
                            };
                          })
                        : [],
                      color: contextVariables?.[color] ? contextVariables?.[color] : '',
                      description: contextVariables?.[description]
                        ? contextVariables?.[description]
                        : '',
                      inline,
                      type,
                      value: contextVariables?.[label] ? contextVariables?.[label] : '',
                      valueNo: valueNo
                        ? formatData({
                            format: '0.0%',
                            value: cardData?.[valueNo],
                          })
                        : '',
                      valueNoLabel: labels?.[valueNo] ? labels?.[valueNo] : '',
                      values:
                        type === 'list' && orientation === 'horizontal' ? values.join(', ') : '',
                      valueYes: valueYes
                        ? formatData({
                            format: '0.0%',
                            value: cardData?.[valueYes],
                          })
                        : '',
                      valueYesLabel: labels?.[valueYes] ? labels?.[valueYes] : '',
                    };
                  }),
                };
              }),
            };
          });
        })
      : cardsLayout?.CardsGroup
        ? data?.map((cardData) => {
            const {
              $: { color = '#65748D', description = '', hasStatusBar = '', id = '', title = '' },
              CardPanels: cardPanels,
            } = cardsGroup;

            const cardId = cardData?.['card'] || id;
            const peopleGroupId = cardId ? peopleGroupOptions?.[cardId] : '';
            const nodePeopleGroupOption = peopleGroupId
              ? getPeopleGroupOption(peopleGroupId, peopleGroupTypes)
              : null;
            const nodeAskWhyOption = peopleGroupId
              ? getAskWhyOption(peopleGroupId, askWhyPeopleGroups)
              : null;

            return [
              {
                color,
                description: contextVariables?.[description] ? contextVariables?.[description] : '',
                hasStatusBar,
                id,
                nodeAskWhyOption,
                nodePeopleGroupOption,
                title: contextVariables?.[title] ? contextVariables?.[title] : '',
                treeRows: cardPanels?.map((cardPanel) => {
                  const {
                    $: { orientation = '' },
                    CardPanel: panels,
                  } = cardPanel;
                  return {
                    orientation,
                    treeCol: panels.map((card) => {
                      const {
                        $: { label = '', type = '' },
                        CardRow: cardRows,
                      } = card;

                      return {
                        cardRows: cardRows
                          ? cardRows?.map((row) => {
                              return {
                                cardCol: row?.CardCol?.map((col) => {
                                  if (col) {
                                    const {
                                      $: { format = '', id = '' },
                                    } = col;
                                    return {
                                      description: labels?.[id] ? labels?.[id] : '',
                                      title: cardData?.[id]
                                        ? formatData({
                                            format: format,
                                            value: cardData?.[id],
                                          })
                                        : '',
                                    };
                                  } else return null;
                                }),
                              };
                            })
                          : [],
                        description: contextVariables?.[description]
                          ? contextVariables?.[description]
                          : '',
                        label,
                        type,
                        value:
                          orientation === 'vertical'
                            ? cardData?.['card_top_title']
                            : contextVariables?.[label],
                      };
                    }),
                  };
                }),
              },
            ];
          })
        : null,
    id,
    name,
    tooManyRows,
  };
};

/**
 * transforms and formats Tree report data
 * @param  {Array} options.askWhyPeopleGroups
 * @param  {XML} options.jsx
 * @param  {Array} options.peopleGroupTypes
 * @param  {Array} options.reportData
 * @return {Object}
 */
export const transformTreeReportData = ({
  askWhyPeopleGroups,
  jsx,
  peopleGroupTypes,
  reportData,
}) => {
  const parser = new XMLParser({
    attributeNamePrefix: '',
    attributesGroupName: '$',
    ignoreAttributes: false,
    ignoreDeclaration: true,
    isArray: (_name, _jpath, _isLeafNode, isAttribute) => !isAttribute,
  });
  const tab = parser.parse(jsx).Tab?.[0];
  const { colLabels: labels = {}, data = [], id = '' } = reportData?.[0] || {};

  const treesLayout = tab?.Tree?.length > 0 ? tab?.Tree[0] : {};

  let rows = [];
  const nodes = treesLayout?.Node
    ? treesLayout?.Node?.filter((blocks) => (blocks?.$?.id ? blocks?.$?.id === id : blocks))
    : [];

  const type = treesLayout?.$?.type;
  const { peopleGroup } = getTreeConfig(type);

  const isLeakageTree = type === 'leakage-tree' || type === 'retailer-leakage-tree';
  const numOfRowsTreeType = {
    'brand-tree-v2': 4,
    'bricks-clicks-tree': 5,
    [DECOMPOSITION_TREE_TYPE]: 2,
    'ebsova-tree': 2,
    'leakage-tree': 4,
    'new-buyers-analysis-tree': 3,
    'new-buyers-tree': 3,
    'new-item-summary-tree': 1,
    'promo-effectiveness-conversion': 3,
    'trip-feedback': 4,
  };

  const rowsNumber = numOfRowsTreeType?.[type] ? numOfRowsTreeType?.[type] : 4;

  return !isLeakageTree
    ? {
        cardBlocks: {
          treeNodes: nodes?.map((treeBlock, index) => {
            const node = treeBlock.$;
            const treeData = data?.[index] ? data[index] : {};
            const maxNumOfRows = treesLayout?.Node?.length > 0 && node?.['num_of_rows'];

            const numOfRows = maxNumOfRows ? parseInt(maxNumOfRows) : rowsNumber;
            rows[index] = [];
            for (let rowNumber = 1; rowNumber < numOfRows + 1; rowNumber += 1) {
              const rowPrefix = `r${rowNumber}`;
              const c1Value = treeData?.[`${rowPrefix}c1_value`];
              const c1Description = treeData?.[`${rowPrefix}c1_description`];
              const c1Format = node?.[`${rowPrefix}c1_format`];
              const c2Value = treeData?.[`${rowPrefix}c2_value`];
              const c2Description = treeData?.[`${rowPrefix}c2_description`];
              const c2Format = node?.[`${rowPrefix}c2_format`];

              let treeCol = [];
              if (
                ((c1Value && c1Value !== 'na') || (c1Description && c1Description !== 'na')) &&
                c1Value !== 'NAN_SENTINEL' &&
                c1Description !== 'NAN_SENTINEL'
              ) {
                if (type === 'trip-feedback' && treeData?.id === 'node_1') {
                  treeCol.push({
                    description:
                      c1Value && c1Value !== 'na'
                        ? formatData({ format: c1Format, value: c1Value })
                        : '',
                    value:
                      c1Description && c1Description !== 'na'
                        ? formatData({ format: c1Format, value: c1Description })
                        : '',
                  });
                } else {
                  treeCol.push({
                    description:
                      c1Description && c1Description !== 'na'
                        ? formatData({ format: c1Format, value: c1Description })
                        : '',
                    value:
                      (c1Value || c1Value === 0) && c1Value !== 'na'
                        ? formatData({ format: c1Format, value: c1Value })
                        : '',
                  });
                }
              }
              if (
                ((c2Value && c2Value !== 'na') || (c2Description && c2Description !== 'na')) &&
                c2Value !== 'NAN_SENTINEL' &&
                c2Description !== 'NAN_SENTINEL'
              ) {
                if (type === 'trip-feedback' && treeData?.id === 'node_1') {
                  treeCol.push({
                    description:
                      c2Value && c2Value !== 'na'
                        ? formatData({ format: c2Format, value: c2Value })
                        : '',
                    value:
                      c2Description && c2Description !== 'na'
                        ? formatData({ format: c2Format, value: c2Description })
                        : '',
                  });
                } else {
                  treeCol.push({
                    description:
                      c2Description && c2Description !== 'na'
                        ? formatData({ format: c2Format, value: c2Description })
                        : '',
                    value:
                      c2Value && c2Value !== 'na'
                        ? formatData({ format: c2Format, value: c2Value })
                        : '',
                  });
                }
              }
              rows[index].push({ treeCol });
            }

            if (type === DECOMPOSITION_TREE_TYPE) {
              rows[index] = transformDecompositionTreeRows({
                node,
                rowCount: numOfRows,
                treeData,
              });
            }

            const nodeId = node?.node_id ? node?.node_id : treeData?.id ? treeData?.id : '';
            const peopleGroupId = peopleGroup?.[nodeId];
            const nodePeopleGroupOption = peopleGroupId
              ? getPeopleGroupOption(peopleGroupId, peopleGroupTypes)
              : null;
            const nodeAskWhyOption = peopleGroupId
              ? getAskWhyOption(peopleGroupId, askWhyPeopleGroups)
              : null;

            return {
              color:
                type === DECOMPOSITION_TREE_TYPE
                  ? getDecompositionNodeColor(getDecompositionPctChange(treeData))
                  : node?.color
                    ? node.color
                    : '#b3c9cb',
              description: treeData?.description ? treeData?.description : '',
              id: treeData?.id ? treeData?.id : '',
              inline: node?.inline ? node?.inline : '',
              nodeAskWhyOption,
              nodeId: node?.node_id ? node?.node_id : treeData?.id ? treeData?.id : '',
              nodePeopleGroupOption,
              nodeRow: node?.node_row ? node?.node_row : '',
              subdescription: treeData?.subdescription ? treeData?.subdescription : '',
              title: treeData?.header ? treeData?.header : '',
              treeRows: rows?.[index] ? rows[index] : [],
            };
          }),
          type: treesLayout?.$?.type ? treesLayout?.$?.type : '',
        },
      }
    : {
        cardBlocks: {
          treeNodes: data.map((treeData, index) => {
            const node = nodes[index]?.$;
            rows[index] = [];
            for (let rowNumber = 1; rowNumber < rowsNumber + 1; rowNumber += 1) {
              const rowKey = node?.[`row_${rowNumber}`];
              const value = rowKey ? treeData?.[rowKey] : null;

              const description = rowKey ? labels?.[rowKey] : null;

              let format = '$0,0';
              if (rowKey === 'change') format = '0.0';
              if (rowKey === 'percent_post' || rowKey === 'percent_spend') format = '0.0%';

              let treeCol = [];
              if (value && description) {
                treeCol.push({
                  description: description ? description : '',
                  value: value ? formatData({ format: format, value: value }) : '',
                });
              }

              rows[index].push({ treeCol });
            }

            const nodeId = node?.node_id;
            const peopleGroupId = peopleGroup?.[nodeId];
            const nodePeopleGroupOption = peopleGroupId
              ? getPeopleGroupOption(peopleGroupId, peopleGroupTypes)
              : null;
            const nodeAskWhyOption = peopleGroupId
              ? getAskWhyOption(peopleGroupId, askWhyPeopleGroups)
              : null;

            return {
              color: node?.color ? node?.color : '#b3c9cb',
              description: treeData?.description ? treeData?.description : '',
              id: treeData?.id ? treeData?.id : '',
              nodeAskWhyOption,
              nodeId: node?.node_id ? node?.node_id : treeData?.id ? treeData?.id : '',
              nodePeopleGroupOption,
              nodeRow: node?.node_row ? node?.node_row : '',
              subdescription: treeData?.subdescription ? treeData?.subdescription : '',
              title: treeData?.title ? treeData?.title : '',
              treeRows: rows?.[index] ? rows?.[index] : [],
            };
          }),
          type,
        },
      };
};

/**
 * transforms and formats Tree report data for Narrative
 * @param  {Array} options.reportData
 * @param  {string} options.type
 * @return {Object}
 */
export const transformTreeNarrativeData = (reportData, type) => {
  return {
    cardBlocks: {
      treeNodes: reportData.map((node, index) => {
        const numRows = Object.keys(node).filter((k) => /^r\d+c1_description$/.test(k)).length;

        let treeRows = [];
        for (let rowNumber = 1; rowNumber <= numRows; rowNumber++) {
          let treeCol = [];
          const c1DescKey = `r${rowNumber}c1_description`;
          const c1FormatKey = `r${rowNumber}c1_format`;
          const c1Description = node[c1DescKey];
          const c1Format = node[c1FormatKey];

          if (
            c1Description !== undefined &&
            c1Description !== null &&
            c1Description !== 'na' &&
            c1Description !== 'NAN_SENTINEL'
          ) {
            treeCol.push({
              description: typeof c1Description === 'string' ? c1Description : '',
              value:
                typeof c1Description === 'number'
                  ? formatData({ format: c1Format, value: c1Description })
                  : '',
            });
          }

          const c2DescKey = `r${rowNumber}c2_description`;
          const c2FormatKey = `r${rowNumber}c2_format`;
          const c2Description = node[c2DescKey];
          const c2Format = node[c2FormatKey];

          if (
            c2Description !== undefined &&
            c2Description !== null &&
            c2Description !== 'na' &&
            c2Description !== 'NAN_SENTINEL'
          ) {
            treeCol.push({
              description: typeof c2Description === 'string' ? c2Description : '',
              value:
                typeof c2Description === 'number'
                  ? formatData({ format: c2Format, value: c2Description })
                  : '',
            });
          }

          treeRows.push({ treeCol });
        }
        return {
          color: node?.color ? node?.color : '#b3c9cb',
          description: node?.description ? node?.description : '',
          id: node?.node_id ? node?.node_id : '',
          inline: node?.inline ? node?.inline : '',
          nodeId: node?.node_id ? node?.node_id : `node_${index + 1}`,
          nodeRow: node?.node_row ? node?.node_row : '',
          subdescription: node?.subdescription ? node?.subdescription : '',
          title: node?.header ? node?.header : '',
          treeRows: treeRows,
        };
      }),
      type,
    },
  };
};

/**
 * transforms and formats Card narrative data
 * @param  {Array} reportData
 * @return {Object}
 */
export const transformCardNarrativeData = (reportData = []) => {
  const normalizedReportData = Array.isArray(reportData)
    ? reportData.filter((cardBlock) => Array.isArray(cardBlock) && cardBlock.length > 0)
    : [];

  return {
    cardBlocks: normalizedReportData.map((cardBlock) => {
      return cardBlock.map((card = {}) => {
        const { panelGroups = [], treeRows: _treeRows, ...restCard } = card;
        const normalizedPanelGroups = Array.isArray(panelGroups) ? panelGroups : [];

        return {
          ...restCard,
          color: card?.color ? card.color : '#b3c9cb',
          description: card?.description ? card.description : '',
          title: card?.title ? card.title : '',
          treeRows: normalizedPanelGroups.map((panelGroup = {}) => {
            const { orientation = '', panels = [] } = panelGroup;
            const normalizedPanels = Array.isArray(panels) ? panels : [];

            return {
              orientation,
              treeCol: normalizedPanels.map((panel = {}) => {
                const {
                  description = '',
                  inline = false,
                  label = '',
                  rows = [],
                  type = '',
                } = panel;
                const normalizedRows = Array.isArray(rows) ? rows : [];

                return {
                  cardRows: normalizedRows.map((row = {}) => {
                    const { cols = [] } = row;
                    const normalizedCols = Array.isArray(cols) ? cols : [];

                    return {
                      cardCol: normalizedCols.map((col = {}) => {
                        const {
                          format = '',
                          isPreviewPlaceholder = false,
                          label: colLabel = '',
                          value,
                        } = col;
                        const hasValue =
                          value !== undefined &&
                          value !== null &&
                          value !== 'na' &&
                          value !== 'NAN_SENTINEL';

                        return {
                          description: colLabel,
                          title: hasValue
                            ? formatData({ format, value })
                            : isPreviewPlaceholder
                              ? '-'
                              : '',
                        };
                      }),
                    };
                  }),
                  description,
                  inline,
                  type,
                  value: label,
                };
              }),
            };
          }),
        };
      });
    }),
  };
};

/**
 * Return tree structure based on the type
 * @param  {string} type
 * @return {Object}
 */
export const getTreeConfig = (type) => {
  switch (type) {
    case 'brand-tree-v2':
      return {
        connectors: [
          renderThreeNodesConnector(2, 2, 2, 4, 2),
          renderThreeNodesConnector(2, 2, 4, 4, 4),
          renderThreeNodesConnector(2, 2, 6, 4, 6),
        ],
        xs: { node_1: 4, node_2: 4, node_3: 4, node_4: 4, node_5: 4, node_6: 4, node_7: 4 },
        xsOffset: { node_1: 2, node_4: 2, node_6: 4 },
      };

    case 'bricks-clicks-tree':
      return {
        connectors: [
          renderThreeNodesConnector(3, 3, 3, 6, 3),
          renderDoubleTwoNodesConnector(1, 1, 2, 5),
          renderDoubleTwoNodesConnector(1, 1, 2, 5),
        ],
        peopleGroup: {
          node_2: 'bricks-clicks-non-online-buyers',
          node_3: 'bricks-clicks-online-buyers',
          node_4: 'bricks-clicks-nothing-else-online-buyers',
          node_5: 'bricks-clicks-online-one-time-buyers',
          node_6: 'bricks-clicks-something-else-online-buyers',
          node_7: 'bricks-clicks-online-more-than-one-time-buyers',
        },
        xs: { node_1: 6, node_2: 6, node_3: 6, node_4: 6, node_5: 6, node_6: 6, node_7: 6 },
        xsOffset: { node_1: 3 },
      };

    case 'customer-lifetime-value-tree':
      return {
        connectors: [
          renderThreeNodesConnector(2, 2, 6, 4, 6),
          renderThreeNodesConnector(2, 2, 4, 4, 4),
          renderTwoNodesConnector(2, 2),
          renderTwoNodesConnector(2, 2),
        ],
        xs: {
          node_1: 4,
          node_2: 4,
          node_3: 4,
          node_4: 4,
          node_5: 4,
          node_6: 4,
          node_7: 4,
        },
        xsOffset: { node_1: 6, node_2: 4, node_4: 2, node_6: 2, node_7: 2 },
      };

    case 'ebsova-tree':
      return {
        connectors: [renderFourNodesConnector(2)],
        xs: { node_1: 4, node_2: 4, node_3: 4, node_4: 4 },
        xsOffset: { node_1: 4 },
      };

    case 'leakage-tree':
      return {
        connectors: [
          renderThreeNodesConnector(2, 2, 6, 4, 6),
          renderThreeNodesConnector(2, 2, 4, 4, 4),
          renderThreeNodesConnector(2, 2, 2, 4, 2),
          renderTwoNodesConnector(),
          renderTwoNodesConnector(),
        ],
        peopleGroup: {
          node_5: 'leakage-tree-non-buyers',
          node_6: 'leakage-tree-closers',
          node_7: 'leakage-tree-non-closers',
        },
        xs: {
          node_1: 4,
          node_2: 4,
          node_3: 4,
          node_4: 4,
          node_5: 4,
          node_6: 4,
          node_7: 4,
          node_8: 4,
          node_9: 4,
        },
        xsOffset: { node_1: 6, node_2: 4, node_4: 2 },
      };

    case 'new-buyers-analysis-tree':
      return {
        connectors: [
          renderThreeNodesConnector(2, 3, 2, 5, 2),
          renderThreeNodesConnector(2, 2, 6, 4, 6),
        ],
        peopleGroup: {
          node_1: 'new-buyers-all',
          node_2: 'new-buyers-repeat',
          node_3: 'new-buyers-new',
          node_4: 'new-buyers-repeat-cat',
          node_5: 'new-buyers-new-cat',
        },
        xs: { node_1: 6, node_2: 5, node_3: 5, node_4: 5, node_5: 5 },
        xsOffset: { node_1: 1, node_4: 2 },
      };

    case 'new-buyers-tree':
      return {
        connectors: [renderThreeNodesConnector(3, 3, 3, 6, 3)],
        peopleGroup: {
          node_1: 'portfolio-engagement-all',
          node_2: 'portfolio-engagement-exist',
          node_3: 'portfolio-engagement-new',
        },
        xs: { node_1: 6, node_2: 6, node_3: 6 },
        xsOffset: { node_1: 3 },
      };

    case 'new-item-summary-tree':
      return {
        connectors: [renderThreeNodesConnector(2, 2, 4, 4, 4), renderTwoNodesConnector(2, 2)],
        peopleGroup: {
          node_1: 'new-item-triers',
          node_2: 'new-item-repeaters',
          node_3: 'new-item-one-time-buyers',
          node_4: 'new-item-multi-time-repeaters',
        },
        xs: { node_1: 4, node_2: 4, node_3: 4, node_4: 4 },
        xsOffset: { node_1: 4, node_2: 2, node_4: 2 },
      };

    case 'promo-effectiveness-conversion':
      return {
        connectors: [
          renderThreeNodesConnector(3, 3, 3, 6, 3),
          renderDoubleTwoNodesConnector(1, 1, 2, 5),
          renderDoubleTwoNodesConnector(1, 1, 2, 5),
          renderRightTwoNodesConnector(1, 8),
        ],
        xs: {
          node_1: 6,
          node_2: 6,
          node_3: 6,
          node_4: 6,
          node_5: 6,
          node_6: 6,
          node_7: 6,
          node_8: 6,
        },
        xsOffset: { node_1: 3, node_8: 6 },
      };

    case 'retailer-leakage-tree':
      return {
        connectors: [
          renderThreeNodesConnector(2, 2, 4, 4, 4),
          renderTwoNodesConnector(2, 2),
          renderTwoNodesConnector(2, 2),
        ],
        peopleGroup: {
          node_2: 'leakage-tree-closers',
          node_3: 'leakage-tree-non-closers',
        },
        xs: { node_1: 4, node_2: 4, node_3: 4, node_4: 4, node_5: 4 },
        xsOffset: { node_1: 4, node_2: 2, node_4: 2, node_5: 2 },
      };

    case 'retailer-trip-circuits-tree':
      return {
        connectors: [
          renderThreeNodesConnector(2, 2, 4, 4, 4),
          renderTwoNodesConnector(2, 2),
          renderTwoNodesConnector(2, 2),
          renderTwoNodesConnector(2, 2),
        ],
        xs: { node_1: 4, node_2: 4, node_3: 4, node_4: 4, node_5: 4, node_6: 4 },
        xsOffset: { node_1: 4, node_2: 2, node_4: 2, node_5: 2, node_6: 2 },
      };

    case 'trip-circuits-tree':
      return {
        connectors: [
          renderThreeNodesConnector(2, 2, 6, 4, 6),
          renderThreeNodesConnector(2, 2, 4, 4, 4),
          renderThreeNodesConnector(2, 2, 2, 4, 2),
          renderTwoNodesConnector(),
          renderTwoNodesConnector(),
        ],
        xs: {
          node_1: 4,
          node_2: 4,
          node_3: 4,
          node_4: 4,
          node_5: 4,
          node_6: 4,
          node_7: 4,
          node_8: 4,
          node_9: 4,
        },
        xsOffset: { node_1: 6, node_2: 4, node_4: 2 },
      };

    case 'trip-feedback':
      return {
        connectors: [renderThreeNodesConnector(3, 3, 3, 6, 3)],
        xs: { node_1: 6, node_2: 6, node_3: 6 },
        xsOffset: { node_1: 3 },
      };

    default:
      return {};
  }
};

/**
 * download PNG for cards and trees
 * @param  {XML} jsx
 * @param  {Array} reportData
 * @return {Object}
 */
export const downloadDiagram = async (elementReference, exportData) => {
  const { reportName, reportType, tabName } = exportData;

  const element = elementReference.current;

  const reportFileName = `${getSanitizedFilename({
    reportName,
    reportType,
    tabName,
  })}_diagram`;

  try {
    const canvas = await createCanvasFromElement(element);
    const padding = 24;
    const paddedCanvas = document.createElement('canvas');
    paddedCanvas.width = canvas.width + padding * 2;
    paddedCanvas.height = canvas.height + padding * 2;
    const context = paddedCanvas.getContext('2d');
    context.drawImage(canvas, padding, padding);
    const link = document.createElement('a');
    document.body.appendChild(link);
    link.download = reportFileName;
    link.href = paddedCanvas.toDataURL();
    link.target = '_blank';
    link.click();
    document.body.removeChild(link);
  } catch (error) {
    console.error(error);
  }
};

/**
 * get Status Bar Style for Heavy-Medium-Light cards
 * @param  {string} color
 * @param  {number} level
 * @param  {number} priority
 * @return {Object}
 */
export const getStatusBarStyle = ({ color, level, priority }) => {
  let heavyStyle;
  let icon;
  let lightStyle;
  let mediumStyle;

  switch (level) {
    case 5:
      //steady
      heavyStyle = { background: priority === 3 ? color : undefined };
      icon = <SquareIcon />;
      lightStyle = { background: priority === 1 ? color : undefined };
      mediumStyle = { background: priority === 2 ? color : undefined };
      break;
    case 6:
      //fast growth
      heavyStyle = { background: color };
      icon = <DoubleArrowIcon />;
      lightStyle = { background: color, opacity: 0.5 };
      mediumStyle = { background: color, opacity: 0.5 };
      break;
    case 7:
      //fast decline
      heavyStyle = { background: color, opacity: 0.5 };
      icon = <DoubleArrowIcon />;
      lightStyle = { background: color };
      mediumStyle = { background: color, opacity: 0.5 };
      break;
    case 8:
      //moderate growth
      heavyStyle = { background: priority === 3 ? color : null };
      icon = <SortUpIcon />;
      lightStyle = {
        background: priority === 2 ? color : undefined,
        opacity: priority === 2 ? 0.5 : 1,
      };
      mediumStyle = { background: color, opacity: priority === 2 ? 1 : 0.5 };
      break;
    case 9:
      //moderate decline
      heavyStyle = { background: priority === 2 ? color : null, opacity: priority === 2 ? 0.5 : 1 };
      icon = <SortUpIcon />;
      lightStyle = { background: priority === 1 ? color : null };
      mediumStyle = { background: color, opacity: priority === 2 ? 1 : 0.5 };
      break;
    case 10:
      //new
      heavyStyle = { background: priority === 3 ? color : undefined };
      icon = <FavoriteFilledIcon />;
      lightStyle = { background: priority === 1 ? color : undefined };
      mediumStyle = { background: priority === 2 ? color : undefined };
      break;
    case 11:
      //lapsed
      heavyStyle = { background: priority === 3 ? color : undefined };
      icon = <CloseBoldIcon />;
      lightStyle = { background: priority === 1 ? color : undefined };
      mediumStyle = { background: priority === 2 ? color : undefined };
      break;
    default:
      break;
  }

  return { heavyStyle, icon, lightStyle, mediumStyle };
};

// Tree types rendered NATIVELY (shape-based) in PPT via mapTreeToExportItem; every other type falls
// back to image capture. getTreeConfig supplies each type's 12-column grid (xs/xsOffset).
const NATIVE_TREE_TYPES = new Set([
  'brand-tree-v2',
  'bricks-clicks-tree',
  'ebsova-tree',
  'leakage-tree',
  'new-item-summary-tree',
  'retailer-leakage-tree',
]);

// Leakage-family trees differ from the brand tree in two ways: (1) each row carries ONE label+value
// cell (vs the brand tree's two single-purpose cells), and (2) the decomposition cascades down the
// FIRST/primary node of each row (vs the brand tree's last node). See mapTreeToExportItem.
const LEAKAGE_TREE_TYPES = new Set(['leakage-tree', 'retailer-leakage-tree']);
const MULTI_METRIC_CELL_TREE_TYPES = new Set(['bricks-clicks-tree', 'new-item-summary-tree']);
const FIRST_ANCHOR_TREE_TYPES = new Set([...LEAKAGE_TREE_TYPES, 'new-item-summary-tree']);

/**
 * Whether a tree `type` is exported as a native (shape-based) slide. Used by the export producers to
 * decide native vs image fallback (kept here so the list lives with the tree mapping).
 * @param  {string} type
 * @return {boolean}
 */
export const isNativeTreeType = (type) => NATIVE_TREE_TYPES.has(type);

/**
 * Maps a transformed tree (transformTreeReportData output) into a native PPTX tree export item. This
 * is a thin Report-shape adapter: it lifts each node's grid span/offset from getTreeConfig and folds
 * the nested `treeRows` into flat { left, right } rows, then hands clean nodes to the shared `treeItem`
 * builder, which owns column-flow layout, spine connectors, and color normalization. Any other feature
 * builds a tree the same way — transform its data into nodes, call `treeItem`.
 *
 * @param  {string} title      slide title (tab name)
 * @param  {Array}  treeNodes  cardBlocks.treeNodes
 * @param  {string} type       tree type (e.g. 'brand-tree-v2') — selects the getTreeConfig grid
 * @return {Object} tree ExportItem (see @utilities/powerpoint treeItem)
 */
export const mapTreeToExportItem = ({ title, treeNodes = [], type }) => {
  const { xs = {}, xsOffset = {} } = getTreeConfig(type);
  const hasLabelValueRows = LEAKAGE_TREE_TYPES.has(type);

  const nodes = treeNodes
    .filter((node) => node?.nodeId)
    .map((node) => ({
      color: node.color || '#b3c9cb',
      description: [node.description, node.subdescription].filter(Boolean).join('\n'),
      gridSpan: xs[node.nodeId] || 4,
      leadingOffset: xsOffset[node.nodeId] || 0,
      nodeId: node.nodeId,
      row: parseInt(node.nodeRow, 10) || 1,
      rows: (node.treeRows || [])
        .flatMap((treeRow) => {
          const cols = treeRow.treeCol || [];
          if (MULTI_METRIC_CELL_TREE_TYPES.has(type)) {
            return cols.map((cell = {}) => ({
              left: cell.description || '',
              right: cell.value || '',
            }));
          }
          // EBSOVA provides its label and formatted value as two cells. Report payloads may instead
          // include a value on the label cell, so retain that form as a fallback.
          if (type === 'ebsova-tree') {
            const [labelCell = {}, valueCell = {}] = cols;
            return {
              left: labelCell.description || '',
              right: labelCell.value || valueCell.value || '',
            };
          }
          // Leakage rows carry ONE cell with both a label and a value → { left: label, right: value }.
          if (hasLabelValueRows) {
            const [labelCell = {}, valueCell = {}] = cols;
            return {
              left: labelCell.description || '',
              right: labelCell.value || valueCell.value || '',
            };
          }
          // Brand rows carry up to two single-purpose cells (label cell, value cell).
          const cells = cols.map((cell) => cell.description || cell.value || '');
          const left = cells[0] || '';
          // the header row repeats the node title as its label — keep only the value there
          return { left: left === (node.title || '') ? '' : left, right: cells[1] || '' };
        })
        .filter((row) => row.left || row.right),
      title: node.title || '',
    }));

  // Leakage trees decompose down the first/primary node of each row; brand trees down the last.
  return treeItem({
    nodes,
    spineAnchor: FIRST_ANCHOR_TREE_TYPES.has(type) ? 'first' : 'last',
    title,
  });
};

/**
 * Flattens a transformed card's nested panel structure (treeRows -> treeCol -> cardRows -> cardCol)
 * into an ordered list of { label, value } metric rows.
 */
const flattenCardRows = (card) => {
  const rows = [];
  (card?.treeRows || []).forEach((panel) => {
    (panel?.treeCol || []).forEach((column) => {
      (column?.cardRows || []).forEach((cardRow) => {
        (cardRow?.cardCol || []).forEach((cell) => {
          if (cell?.description || cell?.title) {
            rows.push({ label: cell.description || '', value: cell.title || '' });
          }
        });
      });
    });
  });
  return rows;
};

/**
 * Maps a transformed CardsLayout (transformCardReportData output) into a native card-grid export item.
 * Thin Report-shape adapter: each CardsBlock becomes a grid row, each Card a column, with the nested
 * panel structure flattened to { label, value } rows. The shared `cardGridItem` builder owns column
 * derivation, color normalization, and defaults — the same builder any feature uses for a card grid.
 *
 * @param  {Array}  cardBlocks transformCardReportData output's cardBlocks (block[] of card[])
 * @param  {string} title      slide title (tab name)
 * @return {Object} cards ExportItem (see @utilities/powerpoint cardGridItem)
 */
export const mapCardGridToExportItem = ({ cardBlocks = [], title }) => {
  const cards = [];

  cardBlocks.forEach((block, rowIndex) => {
    (block || []).forEach((card, colIndex) => {
      cards.push({
        col: colIndex,
        color: card?.color || '#B3C9CB',
        description: card?.description || '',
        row: rowIndex,
        rows: flattenCardRows(card),
        title: card?.title || '',
      });
    });
  });

  return cardGridItem({ cards, fitOnSingleSlide: true, title });
};
